import { useState } from 'react';
import { GoArrowRight } from 'react-icons/go';
import { MdOutlinePlayCircle } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { getAuthUrl } from '@podus/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import ProductShowcase from './ProductShowcase';
import ScrollReveal from './ScrollReveal';

export default function Hero() {
    const authBase = getAuthUrl();
    const [videoOpen, setVideoOpen] = useState(false);

    const { t } = useLanguage();
    return (
        <section className="px-4 lg:px-16 xl:px-20 pt-4 lg:pt-0 min-h-[calc(100dvh-68px)] lg:min-h-[calc(100dvh-76px)] flex flex-col">
            <div className="relative rounded-3xl lg:rounded-[2rem] overflow-hidden flex-1 flex flex-col bg-[#F5F5F5]">
                <div className="flex flex-col lg:flex-row items-center lg:items-center justify-center px-5 lg:px-16 pt-10 pb-10 lg:py-20 flex-1">
                    {/* Grid pattern — centered in main container */}
                    <img
                        src="/assets/libs/grid-patterns.svg"
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 m-auto w-1/2 h-1/2 object-contain pointer-events-none"
                    />

                    {/* Left Content */}
                    <div className="lg:flex-1 lg:max-w-[55%] w-full relative">
                        {/* Social Proof Badge */}
                        <ScrollReveal variant="fade-up" delay={100} distance={24}>
                            <div className="inline-flex items-center gap-3 bg-white rounded-[20px] pl-4 pr-3 py-2.5 border border-gray-100">
                                <div>
                                    <span className="text-primary font-bold text-sm leading-tight block">
                                        {t('hero.socialProof')}
                                    </span>
                                    <p className="text-secondary-100 text-[11px] font-normal leading-tight">
                                        {t('hero.socialProofText')}
                                    </p>
                                </div>
                                <div className="flex items-center -space-x-2">
                                    <img
                                        src="/tem/avatar1.png"
                                        alt="User avatar 1"
                                        className="h-8.5 w-8.5 rounded-full border-2 border-white object-cover"
                                    />
                                    <img
                                        src="/tem/avatar2.png"
                                        alt="User avatar 2"
                                        className="h-8.5 w-8.5 rounded-full border-2 border-white object-cover"
                                    />
                                    <img
                                        src="/tem/avatar3.png"
                                        alt="User avatar 3"
                                        className="h-8.5 w-8.5 rounded-full border-2 border-white object-cover"
                                    />
                                    <img
                                        src="/tem/avatar4.png"
                                        alt="User avatar 4"
                                        className="h-8.5 w-8.5 rounded-full border-2 border-white object-cover"
                                    />
                                    <div className="h-8.5 w-8.5 rounded-full bg-secondary text-white border-2 border-white flex items-center justify-center text-[15px] font-semibold">
                                        <FaPlus size={14} className="text-primary" />
                                    </div>
                                </div>
                                <GoArrowRight className="w-5 h-5 text-primary ml-1" />
                            </div>
                        </ScrollReveal>

                        {/* Main Heading */}
                        <ScrollReveal variant="blur-up" delay={250} duration={900} distance={32}>
                            <h1 className="font-bold mt-8 lg:mt-10 text-[36px] sm:text-[48px] lg:text-[80px] xl:text-[96px] leading-[1.08] tracking-tight">
                                {/* Visible eyebrow — puts the priority ranking terms in the H1 text itself.
                                    Kept in English (the crawled/default render); not translated. */}
                                <span className="mb-3 block text-[12px] font-semibold uppercase tracking-[0.18em] text-secondary sm:text-[14px]">
                                    Access to Justice
                                </span>
                                <span className="text-[#AEAEAE]">{t('hero.heading1')}</span>
                                <span className="text-secondary">{t('hero.heading2')}</span>
                            </h1>
                        </ScrollReveal>

                        {/* Subtitle */}
                        <ScrollReveal variant="fade-up" delay={450} distance={24}>
                            <p className="mt-6 text-secondary text-base lg:text-[17px] max-w-xl leading-relaxed font-normal">
                                {t('hero.subtitle')}
                            </p>
                        </ScrollReveal>

                        {/* CTA Buttons */}
                        <ScrollReveal variant="fade-up" delay={600} distance={20}>
                            <div className="mt-10 lg:mt-8 flex flex-col sm:flex-row gap-4">
                                <a
                                    href={`${authBase}`}
                                    className="bg-secondary text-white rounded-full py-4 sm:w-[262px] sm:h-[53px] sm:py-0 flex items-center justify-center text-base font-medium text-center hover:bg-secondary-400 transition-colors"
                                >
                                    {t('hero.ctaGetStarted')}
                                </a>
                                <button
                                    onClick={() => setVideoOpen(true)}
                                    className="flex items-center justify-center gap-3 whitespace-nowrap border border-[#1C1C1F] rounded-full px-6 py-4 sm:py-3.5 text-sm sm:text-base font-medium text-secondary hover:bg-gray-50 transition-colors"
                                >
                                    {t('hero.ctaHowItWorks')}
                                    <span className="flex items-center justify-center flex-shrink-0">
                                        <MdOutlinePlayCircle className="w-8 h-8 sm:w-7 sm:h-7 text-primary ml-0.5" />
                                    </span>
                                </button>
                            </div>
                        </ScrollReveal>
                    </div>

                    {/* Right Content — 3D product orbit (falls back to a static grid) */}
                    <div className="mt-10 lg:mt-0 lg:flex flex-1 justify-end items-stretch self-stretch lg:pl-8">
                        <ProductShowcase />
                    </div>
                </div>
            </div>

            {/* Video Modal */}
            {videoOpen && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm"
                    onClick={() => setVideoOpen(false)}
                >
                    <div
                        className="relative w-[90vw] max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setVideoOpen(false)}
                            className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
                            aria-label="Close video"
                        >
                            <IoClose className="w-8 h-8" />
                        </button>
                        <iframe
                            src="https://www.youtube.com/embed/TU1pBykmTmw?autoplay=1"
                            title="How Podus Works"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                        />
                    </div>
                </div>
            )}
        </section>
    );
}
